import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from 'use-toast-mui';

import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  TabGroup,
  Tab,
  DescriptionText,
  RadioOptionList,
  RadioOption,
  StyledRadio,
  RadioLabel,
  CustomInputWrapper,
  SmallInput,
  UnitSegmentedControl,
  ModalFooter,
} from './StandardModalStyled';
import CommonModalButton from '../../../../form-components/CommonModalButton';

// 탭 타입 상수 (UI 전용)
const TAB_TYPES = {
  USAGE: 'USAGE', // 이용권
  COURSE: 'COURSE', // 수강권
  LOCKER: 'LOCKER', // 라커
};

// 탭 → 백엔드 필드명 매핑
const TAB_TO_FIELD = {
  [TAB_TYPES.USAGE]: 'passAlertDays',
  [TAB_TYPES.COURSE]: 'ticketAlertCount',
  [TAB_TYPES.LOCKER]: 'lockerAlertDays',
};

// 탭 → 단위 필드명 매핑 (FIXED=회/일, PERCENT=총량 대비 %)
const TAB_TO_UNIT_FIELD = {
  [TAB_TYPES.USAGE]: 'passAlertDaysUnit',
  [TAB_TYPES.COURSE]: 'ticketAlertCountUnit',
  [TAB_TYPES.LOCKER]: 'lockerAlertDaysUnit',
};

// 수강권 기간 필드 (신규)
const COURSE_DAYS_FIELD = 'ticketAlertDays';
const COURSE_DAYS_UNIT_FIELD = 'ticketAlertDaysUnit';

const UNIT_FIXED = 'FIXED';
const UNIT_PERCENT = 'PERCENT';
const MAX_PERCENT = 100;
const MAX_FIXED = 365; // 백엔드 검증(1~365)과 동일 상한

// 단위별 상한으로 입력값 문자열을 클램프한다 (빈 문자열은 그대로)
const clampCustomValue = (raw, unit) => {
  if (!raw) return raw;
  const max = unit === UNIT_PERCENT ? MAX_PERCENT : MAX_FIXED;
  return Number(raw) > max ? String(max) : raw;
};

// 단위 전환 시 직접입력에 넣을 값. 프리셋 상태였다면 프리셋 값을 승계한다.
const seedForUnitChange = (customDays, isCustom, selectedOption, unit) =>
  clampCustomValue(customDays || (!isCustom && selectedOption != null ? String(selectedOption) : ''), unit);

// 직접입력인데 값이 비었거나 0이면 저장 불가 (조용히 기본값으로 회귀하는 사고 방지)
const hasInvalidCustomValue = data => data.isCustom && !(Number(data.customDays) > 0);

// 기본값 (백엔드 명세와 동일: 수강권 3회, 이용권 30일, 라커 30일)
const DEFAULT_VALUES = {
  [TAB_TYPES.USAGE]: 30,
  [TAB_TYPES.COURSE]: 3,
  [TAB_TYPES.LOCKER]: 30,
};

const DEFAULT_COURSE_DAYS = 30;

// 탭별 라디오 옵션
const TAB_OPTIONS = {
  [TAB_TYPES.USAGE]: { options: [1, 7, 15, 30], unit: '일' },
  [TAB_TYPES.COURSE]: { options: [1, 3, 5, 10], unit: '회' },
  [TAB_TYPES.LOCKER]: { options: [1, 7, 15, 30], unit: '일' },
};

// 수강권 기간 옵션
const COURSE_DAYS_OPTIONS = { options: [1, 7, 15, 30], unit: '일' };

const toUnit = raw => (raw === UNIT_PERCENT ? UNIT_PERCENT : UNIT_FIXED);

// 백엔드 응답 형식 → 내부 탭 state로 변환 (PERCENT 저장값은 직접입력+% 상태로 복원)
const toTabData = apiData => {
  const result = {};
  Object.values(TAB_TYPES).forEach(tab => {
    const value = apiData?.[TAB_TO_FIELD[tab]] ?? DEFAULT_VALUES[tab];
    const unit = toUnit(apiData?.[TAB_TO_UNIT_FIELD[tab]]);
    const options = TAB_OPTIONS[tab].options;
    const isPreset = unit === UNIT_FIXED && options.includes(value);
    result[tab] = {
      selectedOption: isPreset ? value : options[0],
      customDays: isPreset ? '' : String(value),
      isCustom: !isPreset,
      customUnit: unit,
    };
  });
  return result;
};

// 수강권 기간 state 초기화
const toCourseDaysData = apiData => {
  const value = apiData?.[COURSE_DAYS_FIELD] ?? DEFAULT_COURSE_DAYS;
  const unit = toUnit(apiData?.[COURSE_DAYS_UNIT_FIELD]);
  const options = COURSE_DAYS_OPTIONS.options;
  const isPreset = unit === UNIT_FIXED && options.includes(value);
  return {
    selectedOption: isPreset ? value : options[0],
    customDays: isPreset ? '' : String(value),
    isCustom: !isPreset,
    customUnit: unit,
  };
};

// 한 섹션 state → {value, unit}. 직접입력 값이 비어 있으면 기본값(FIXED)으로 폴백.
const resolveEntry = (data, fallbackValue) => {
  if (data.isCustom && Number(data.customDays) > 0) {
    const unit = data.customUnit;
    const raw = Number(clampCustomValue(data.customDays, unit));
    return { value: raw, unit };
  }
  if (data.isCustom) {
    return { value: fallbackValue, unit: UNIT_FIXED };
  }
  return { value: data.selectedOption, unit: UNIT_FIXED };
};

// 내부 탭 state → 백엔드 payload로 변환
const toApiPayload = (tabData, courseDaysData) => {
  const payload = {};
  Object.values(TAB_TYPES).forEach(tab => {
    const { value, unit } = resolveEntry(tabData[tab], DEFAULT_VALUES[tab]);
    payload[TAB_TO_FIELD[tab]] = value;
    payload[TAB_TO_UNIT_FIELD[tab]] = unit;
  });
  // 수강권 기간 추가
  const { value: daysValue, unit: daysUnit } = resolveEntry(courseDaysData, DEFAULT_COURSE_DAYS);
  payload[COURSE_DAYS_FIELD] = daysValue;
  payload[COURSE_DAYS_UNIT_FIELD] = daysUnit;
  return payload;
};

const StandardModal = ({ open, onClose, onSubmit, initialData }) => {
  const { t } = useTranslation();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState(TAB_TYPES.USAGE);
  const [selectedOption, setSelectedOption] = useState(DEFAULT_VALUES[TAB_TYPES.USAGE]);
  const [customDays, setCustomDays] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [customUnit, setCustomUnit] = useState(UNIT_FIXED);
  const [tabData, setTabData] = useState(() => toTabData(null));

  // 수강권 기간 state
  const [courseDays, setCourseDays] = useState(() => toCourseDaysData(null));

  useEffect(() => {
    if (!open) return;
    const next = toTabData(initialData);
    setTabData(next);
    setCourseDays(toCourseDaysData(initialData));
    setActiveTab(TAB_TYPES.USAGE);
    const first = next[TAB_TYPES.USAGE];
    setSelectedOption(first.selectedOption);
    setCustomDays(first.customDays);
    setIsCustom(first.isCustom);
    setCustomUnit(first.customUnit);
  }, [open, initialData]);

  const handleTabChange = newTab => {
    setTabData(prev => ({
      ...prev,
      [activeTab]: { selectedOption, customDays, isCustom, customUnit },
    }));
    const newTabData = tabData[newTab];
    setSelectedOption(newTabData.selectedOption);
    setCustomDays(newTabData.customDays);
    setIsCustom(newTabData.isCustom);
    setCustomUnit(newTabData.customUnit);
    setActiveTab(newTab);
  };

  const handleOptionSelect = days => {
    setSelectedOption(days);
    setIsCustom(false);
    setCustomDays('');
    // 직접입력 상태를 완전히 초기화 — % 단위가 남아 있으면 다음 직접입력에 조용히 새어든다
    setCustomUnit(UNIT_FIXED);
  };

  const handleCustomSelect = () => {
    setIsCustom(true);
    setSelectedOption(null);
  };

  const handleCustomDaysChange = e => {
    const digits = e.target.value.replace(/[^0-9]/g, '');
    setCustomDays(clampCustomValue(digits, customUnit));
  };

  const handleCustomUnitChange = unit => {
    setCustomUnit(unit);
    setCustomDays(seedForUnitChange(customDays, isCustom, selectedOption, unit));
    handleCustomSelect();
  };

  // 수강권 기간 핸들러
  const handleCourseDaysOptionSelect = days => {
    setCourseDays(prev => ({
      ...prev,
      selectedOption: days,
      customDays: '',
      isCustom: false,
      customUnit: UNIT_FIXED,
    }));
  };

  const handleCourseDaysCustomSelect = () => {
    setCourseDays(prev => ({ ...prev, isCustom: true, selectedOption: null }));
  };

  const handleCourseDaysCustomChange = e => {
    const digits = e.target.value.replace(/[^0-9]/g, '');
    setCourseDays(prev => ({ ...prev, customDays: clampCustomValue(digits, prev.customUnit) }));
  };

  const handleCourseDaysUnitChange = unit => {
    setCourseDays(prev => ({
      ...prev,
      isCustom: true,
      selectedOption: null,
      customUnit: unit,
      customDays: seedForUnitChange(prev.customDays, prev.isCustom, prev.selectedOption, unit),
    }));
  };

  const getTabLabel = tabType => {
    switch (tabType) {
      case TAB_TYPES.USAGE:
        return t('이용권');
      case TAB_TYPES.COURSE:
        return t('수강권');
      case TAB_TYPES.LOCKER:
        return t('라커');
      default:
        return '';
    }
  };

  const getDescriptionLabel = () => {
    switch (activeTab) {
      case TAB_TYPES.USAGE:
        return t('이용권 만료임박기준');
      case TAB_TYPES.COURSE:
        return t('수강권 만료임박기준');
      case TAB_TYPES.LOCKER:
        return t('라커 만료임박기준');
      default:
        return '';
    }
  };

  const handleSubmit = () => {
    const finalTabData = {
      ...tabData,
      [activeTab]: { selectedOption, customDays, isCustom, customUnit },
    };
    if (Object.values(finalTabData).some(hasInvalidCustomValue) || hasInvalidCustomValue(courseDays)) {
      toast.error(t('직접입력 값을 입력해 주세요.'));
      return;
    }
    const payload = toApiPayload(finalTabData, courseDays);
    onSubmit?.(payload);
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalOverlay onClick={handleClose}>
        <ModalContainer onClick={e => e.stopPropagation()}>
          {/* Header */}
          <ModalHeader>
            <ModalTitle>{t('만료임박기준 설정')}</ModalTitle>
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>

          {/* Content */}
          <ModalContent>
            {/* Tabs */}
            <TabGroup>
              {Object.values(TAB_TYPES).map(tabType => (
                <Tab key={tabType} isActive={activeTab === tabType} onClick={() => handleTabChange(tabType)}>
                  {getTabLabel(tabType)}
                </Tab>
              ))}
            </TabGroup>

            {/* Description */}
            <DescriptionText>
              {getDescriptionLabel()}
              {activeTab === TAB_TYPES.COURSE && ` (${t('잔여횟수')})`}
              <span className="required">*</span>{' '}
              <span className="sub-text">{t('만료임박상태를 알리는 기준을 설정해 주세요.')}</span>
            </DescriptionText>

            {/* Radio Options */}
            <RadioOptionList>
              {TAB_OPTIONS[activeTab].options.map(value => (
                <RadioOption
                  key={value}
                  isSelected={!isCustom && selectedOption === value}
                  onClick={() => handleOptionSelect(value)}>
                  <StyledRadio checked={!isCustom && selectedOption === value} />
                  <RadioLabel>
                    {value} {t(`${TAB_OPTIONS[activeTab].unit} 이하`)}
                  </RadioLabel>
                </RadioOption>
              ))}

              {/* Custom Input Option — 단위(회/일 ↔ %) 선택 가능 */}
              <RadioOption isSelected={isCustom} onClick={handleCustomSelect}>
                <StyledRadio checked={isCustom} />
                <CustomInputWrapper>
                  <RadioLabel>{t('직접입력')}</RadioLabel>
                  <SmallInput
                    value={customDays}
                    onChange={handleCustomDaysChange}
                    onClick={e => {
                      e.stopPropagation();
                      handleCustomSelect();
                    }}
                    placeholder="00"
                  />
                  <UnitSegmentedControl
                    options={[
                      { value: UNIT_FIXED, label: t(TAB_OPTIONS[activeTab].unit) },
                      { value: UNIT_PERCENT, label: '%' },
                    ]}
                    value={customUnit}
                    onChange={handleCustomUnitChange}
                    onClick={e => e.stopPropagation()}
                  />
                  <RadioLabel>
                    {customUnit === UNIT_PERCENT
                      ? t('남았을 때 만료임박 (전체 대비)')
                      : t('남았을 때 만료임박')}
                  </RadioLabel>
                </CustomInputWrapper>
              </RadioOption>
            </RadioOptionList>

            {/* 수강권 탭일 때 기간 기준 추가 */}
            {activeTab === TAB_TYPES.COURSE && (
              <>
                <DescriptionText style={{ marginTop: 24 }}>
                  {t('수강권 만료임박기준')} ({t('잔여기간')})
                  <span className="required">*</span>{' '}
                  <span className="sub-text">{t('만료일까지 남은 기간 기준을 설정해 주세요.')}</span>
                </DescriptionText>

                <RadioOptionList>
                  {COURSE_DAYS_OPTIONS.options.map(value => (
                    <RadioOption
                      key={`days-${value}`}
                      isSelected={!courseDays.isCustom && courseDays.selectedOption === value}
                      onClick={() => handleCourseDaysOptionSelect(value)}>
                      <StyledRadio checked={!courseDays.isCustom && courseDays.selectedOption === value} />
                      <RadioLabel>
                        {value} {t('일 이하')}
                      </RadioLabel>
                    </RadioOption>
                  ))}

                  <RadioOption isSelected={courseDays.isCustom} onClick={handleCourseDaysCustomSelect}>
                    <StyledRadio checked={courseDays.isCustom} />
                    <CustomInputWrapper>
                      <RadioLabel>{t('직접입력')}</RadioLabel>
                      <SmallInput
                        value={courseDays.customDays}
                        onChange={handleCourseDaysCustomChange}
                        onClick={e => {
                          e.stopPropagation();
                          handleCourseDaysCustomSelect();
                        }}
                        placeholder="00"
                      />
                      <UnitSegmentedControl
                        options={[
                          { value: UNIT_FIXED, label: t('일') },
                          { value: UNIT_PERCENT, label: '%' },
                        ]}
                        value={courseDays.customUnit}
                        onChange={handleCourseDaysUnitChange}
                        onClick={e => e.stopPropagation()}
                      />
                      <RadioLabel>
                        {courseDays.customUnit === UNIT_PERCENT
                          ? t('남았을 때 만료임박 (전체 기간 대비)')
                          : t('남았을 때 만료임박')}
                      </RadioLabel>
                    </CustomInputWrapper>
                  </RadioOption>
                </RadioOptionList>
              </>
            )}
          </ModalContent>

          {/* Footer */}
          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={handleClose}>
              {t('취소')}
            </CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleSubmit}>
              {t('저장')}
            </CommonModalButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    </Modal>
  );
};

export default StandardModal;
